import { themeGet } from '@styled-system/theme-get';
import { BoxChecked, BoxEmpty, Check, RadioChecked, RadioEmpty } from 'pcln-icons';
import React from 'react';
import styled from 'styled-components';
import { Box } from '../Box/Box';
import { Flex } from '../Flex/Flex';
import { Toggle } from '../Toggle/Toggle';
import { getPaletteColor } from '../utils/utils';
/**
 * @public
 */
export const RadioCheckToggleCardCardTypes = ['radio', 'checkbox', 'toggle'];
/**
 * @public
 */
export const RadioCheckToggleCardHPositions = ['left', 'right'];
/**
 * @public
 */
export const RadioCheckToggleCardVPositions = ['top', 'center'];
const CombinedInput = /*#__PURE__*/styled.input.withConfig({
  displayName: "RadioCheckToggleCard__CombinedInput",
  componentId: "sc-1s16dkq-0"
})(["appearance:none;opacity:0;position:absolute;z-index:0;&:hover + div{border-color:", ";& svg{color:", ";}}&:focus-visible + div div.cardIcon > svg{border-color:", ";box-shadow:inset 0 0 0 5px ", ";}&:active + div,&:checked:hover + div{border-color:", ";& div.cardIcon > svg{color:", ";}}&:checked + div{background-color:", ";border:1px solid ", ";& div.cardIcon > svg{color:", ";}}&:checked:focus-visible + div div.cardIcon > svg{border-color:", ";}"], getPaletteColor('primary.base'), getPaletteColor('primary.base'), getPaletteColor('text.light'), getPaletteColor('background.light'), getPaletteColor('primary.dark'), getPaletteColor('primary.dark'), getPaletteColor('primary.light'), getPaletteColor('primary.base'), getPaletteColor('primary.base'), getPaletteColor('primary.dark'));
const RCTCardContainer = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "RadioCheckToggleCard__RCTCardContainer",
  componentId: "sc-1s16dkq-1"
})(["border:1px solid transparent;border-radius:", ";box-shadow:", ";cursor:pointer;padding:", ";"], themeGet('borderRadii.action-lg'), themeGet('shadows.sm'), themeGet('space.3'));
const CardHeader = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "RadioCheckToggleCard__CardHeader",
  componentId: "sc-1s16dkq-2"
})(["min-width:0;width:100%;& + *{margin-top:", ";}"], themeGet('space.3'));
const StyledTitle = /*#__PURE__*/styled(Box).withConfig({
  displayName: "RadioCheckToggleCard__StyledTitle",
  componentId: "sc-1s16dkq-3"
})(["font-weight:", ";font-size:", ";line-height:24px;"], props => props.isTitleBold ? '700' : '500', props => props.isTitleBold ? '16px' : '14px');
const CardContent = /*#__PURE__*/styled(Box).withConfig({
  displayName: "RadioCheckToggleCard__CardContent",
  componentId: "sc-1s16dkq-4"
})(["flex:1;& > :first-child + *{margin-top:", ";}"], themeGet('space.3'));
const CardIcon = /*#__PURE__*/styled(Box).withConfig({
  displayName: "RadioCheckToggleCard__CardIcon",
  componentId: "sc-1s16dkq-5"
})(["line-height:0px;margin:", ";& > svg{border:1px solid transparent;border-radius:", ";color:", ";padding:2px;}"], props => props.hPosition === 'left' ? `0 16px 0 0` : `0 0 0 16px`, props => props.cardType === 'checkbox' ? '4px' : '9999px', getPaletteColor('text.light'));
const RadioIcon = ({
  checked,
  ...props
}) => {
  return checked ? React.createElement(RadioChecked, {
    ...props
  }) : React.createElement(RadioEmpty, {
    ...props
  });
};
const CheckboxIcon = ({
  checked,
  ...props
}) => {
  return checked ? React.createElement(BoxChecked, {
    ...props
  }) : React.createElement(BoxEmpty, {
    ...props
  });
};
const ToggleIcon = ({
  checked,
  onChange,
  ...props
}) => {
  const checkIcon = React.createElement(Check, {
    size: '16'
  });
  return React.createElement(Box, {
    ...props
  }, React.createElement(Toggle, {
    height: 24,
    isOn: checked,
    icon: checkIcon,
    onToggle: onChange
  }));
};
const buttonIcon = cardType => {
  if (cardType === 'radio') {
    return RadioIcon;
  } else if (cardType === 'checkbox') {
    return CheckboxIcon;
  } else if (cardType === 'toggle') {
    return ToggleIcon;
  }
};
/**
 * @public
 */
export function RadioCheckToggleCard({
  backgroundColor = 'background.lightest',
  cardType = 'radio',
  children,
  hPosition = 'right',
  isSelected,
  isTakingFullHeightOfCard = false,
  isTitleBold = false,
  name,
  onChange = () => {},
  title,
  value,
  vPosition = 'top'
}) {
  const id = `${name}_${value}`;
  const ButtonIcon = buttonIcon(cardType);
  return React.createElement("label", {
    htmlFor: id,
    "data-testId": 'rdt-card-label'
  }, React.createElement(CombinedInput, {
    role: cardType === 'radio' ? 'radio' : 'checkbox',
    id: id,
    name: name,
    value: value,
    type: cardType === 'radio' ? 'radio' : 'checkbox',
    onChange: onChange,
    checked: isSelected
  }), React.createElement(RCTCardContainer, {
    flexDirection: 'column',
    "data-testId": 'rdt-card-main',
    color: backgroundColor
  }, React.createElement(CardHeader, {
    alignItems: vPosition,
    flexDirection: hPosition === 'left' ? 'row-reverse' : 'row',
    "data-testId": 'rdt-card-header'
  }, React.createElement(CardContent, null, React.createElement(StyledTitle, {
    isTitleBold: isTitleBold,
    "data-testId": 'rdt-card-title'
  }, title), isTakingFullHeightOfCard && children), React.createElement(CardIcon, {
    className: 'cardIcon',
    cardType: cardType,
    hPosition: hPosition
  }, React.createElement(ButtonIcon, {
    checked: isSelected,
    onChange: onChange,
    size: '24'
  }))), !isTakingFullHeightOfCard && children));
}
RadioCheckToggleCard.displayName = 'RadioCheckToggleCard';